import { useState, useRef, useEffect } from 'react'
import { MoveIcon, LoaderCircleIcon } from 'lucide-react'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { useLingui } from '@lingui/react/macro'

export type ImageEditorTexts = {
  title: string
  description: string
  cancel: string
  save: string
  uploading: string
}

interface CropArea {
  x: number
  y: number
  width: number
  height: number
}

type ResizeHandle =
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right'
  | 'top'
  | 'right'
  | 'bottom'
  | 'left'
  | 'move'
  | null

const MAX_FILE_SIZE = 4 * 1024 * 1024 // 4MB in bytes

/** Kept as data so every handle gets the same role / aria-label / `data-testid`, which is what lets e2e grab one. */
const RESIZE_HANDLES: { key: Exclude<ResizeHandle, 'move' | null>; className: string }[] = [
  { key: 'top-left', className: 'bg-primary absolute -top-2 -left-2 size-4 cursor-nwse-resize rounded-full' },
  { key: 'top-right', className: 'bg-primary absolute -top-2 -right-2 size-4 cursor-nesw-resize rounded-full' },
  { key: 'bottom-left', className: 'bg-primary absolute -bottom-2 -left-2 size-4 cursor-nesw-resize rounded-full' },
  { key: 'bottom-right', className: 'bg-primary absolute -right-2 -bottom-2 size-4 cursor-nwse-resize rounded-full' },
  {
    key: 'top',
    className: 'bg-primary absolute -top-2 left-1/2 h-4 w-8 -translate-x-1/2 cursor-ns-resize rounded-full',
  },
  {
    key: 'right',
    className: 'bg-primary absolute top-1/2 -right-2 h-8 w-4 -translate-y-1/2 cursor-ew-resize rounded-full',
  },
  {
    key: 'bottom',
    className: 'bg-primary absolute -bottom-2 left-1/2 h-4 w-8 -translate-x-1/2 cursor-ns-resize rounded-full',
  },
  {
    key: 'left',
    className: 'bg-primary absolute top-1/2 -left-2 h-8 w-4 -translate-y-1/2 cursor-ew-resize rounded-full',
  },
]

export function ImageEditor(props: {
  open: boolean
  file: File | null
  onSave: (file: File, cropArea: CropArea) => void
  onOpenChange: (open: boolean) => void
  aspectRatio?: { width: number; height: number }
  initialCropArea?: CropArea
}) {
  const { t } = useLingui()

  const aspectWidth = props.aspectRatio?.width
  const aspectHeight = props.aspectRatio?.height

  const texts = {
    title: t`Edit Image`,
    description: props.aspectRatio
      ? t`Drag the corners and edges to crop the image. Aspect ratio is locked to ${aspectWidth}:${aspectHeight}. Drag the center to move the crop area.`
      : t`Drag the corners and edges to crop the image. Drag the center to move the crop area.`,
    cancel: t`Cancel`,
    save: t`Save`,
    uploading: t`Uploading...`,
  }

  const handleLabels: Record<Exclude<ResizeHandle, 'move' | null>, string> = {
    'top-left': t`Resize crop from the top-left corner`,
    'top-right': t`Resize crop from the top-right corner`,
    'bottom-left': t`Resize crop from the bottom-left corner`,
    'bottom-right': t`Resize crop from the bottom-right corner`,
    'top': t`Resize crop from the top edge`,
    'right': t`Resize crop from the right edge`,
    'bottom': t`Resize crop from the bottom edge`,
    'left': t`Resize crop from the left edge`,
  }

  const [imageSrc, setImageSrc] = useState('')
  const [cropArea, setCropArea] = useState({ x: 0, y: 0, width: 0, height: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const [resizeHandle, setResizeHandle] = useState<ResizeHandle>(null)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const [initialCropArea, setInitialCropArea] = useState({ x: 0, y: 0, width: 0, height: 0 })
  const [isSaving, setIsSaving] = useState(false)
  const [imageLoaded, setImageLoaded] = useState(false) // ✅ Track when image is rendered
  const [imageRect, setImageRect] = useState<DOMRect | null>(null) // ✅ Track image bounds
  // Captured in state, not read off the ref during render — a render-time ref read is a React Compiler bail-out.
  const [naturalSize, setNaturalSize] = useState<{ width: number; height: number } | null>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const imageRef = useRef<HTMLImageElement>(null)

  useEffect(() => {
    if (!props.file) return

    // oxlint-disable-next-line react/set-state-in-effect -- external resource sync: resets editor state for a newly-picked file AND creates/revokes its blob URL, which must not run during render
    setImageLoaded(false) // ✅ Reset when new file loads
    setImageRect(null) // ✅ Reset image bounds
    setNaturalSize(null)
    const url = URL.createObjectURL(props.file)
    setImageSrc(url)

    const img = new Image()

    const handleLoad = () => {
      if (props.initialCropArea) {
        setCropArea(props.initialCropArea)
        setInitialCropArea(props.initialCropArea) // ✅ Also set the initialCropArea state for dragging
        return
      }

      const aspectRatio = props.aspectRatio ? props.aspectRatio.width / props.aspectRatio.height : null

      let width = img.width
      let height = img.height
      let x = 0
      let y = 0

      if (aspectRatio) {
        const imageAspectRatio = img.width / img.height

        if (imageAspectRatio > aspectRatio) {
          width = img.height * aspectRatio
          height = img.height
          x = (img.width - width) / 2
        } else {
          width = img.width
          height = img.width / aspectRatio
          y = (img.height - height) / 2
        }
      }

      const defaultCropArea = { x, y, width, height }
      setCropArea(defaultCropArea)
      setInitialCropArea(defaultCropArea) // ✅ Set initial crop area for dragging
    }

    img.addEventListener('load', handleLoad)
    img.src = url

    return () => {
      URL.revokeObjectURL(url)
      img.removeEventListener('load', handleLoad)
    }
  }, [props.file, props.aspectRatio, props.initialCropArea])

  useEffect(() => {
    if (!imageRef.current || !imageSrc) return

    const img = imageRef.current

    const handleImageLoad = () => {
      // Wait for dialog animation to complete before calculating dimensions
      setTimeout(() => {
        setImageLoaded(true)
        if (imageRef.current) {
          setImageRect(imageRef.current.getBoundingClientRect())
          setNaturalSize({ width: imageRef.current.naturalWidth, height: imageRef.current.naturalHeight })
        }
      }, 150) // Wait for dialog animation (typically 150ms)
    }

    if (img.complete && img.naturalWidth > 0) {
      handleImageLoad()
    } else {
      img.addEventListener('load', handleImageLoad)
      return () => img.removeEventListener('load', handleImageLoad)
    }
  }, [imageSrc])

  useEffect(() => {
    if (!imageRef.current || !imageLoaded) return

    const handleResize = () => {
      if (imageRef.current) {
        setImageRect(imageRef.current.getBoundingClientRect())
      }
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [imageLoaded])

  useEffect(() => {
    if (!isDragging) return

    const handleGlobalMouseMove = (e: MouseEvent) => {
      if (!imageRef.current) return

      const img = imageRef.current
      const imgRect = img.getBoundingClientRect()
      const scaleX = img.naturalWidth / imgRect.width
      const scaleY = img.naturalHeight / imgRect.height

      const deltaX = (e.clientX - dragStart.x) * scaleX
      const deltaY = (e.clientY - dragStart.y) * scaleY

      const aspectRatio = props.aspectRatio ? props.aspectRatio.width / props.aspectRatio.height : null
      const minWidth = props.aspectRatio ? props.aspectRatio.width : 50
      const minHeight = props.aspectRatio ? props.aspectRatio.height : 50

      setCropArea(() => {
        const prev = initialCropArea // Use initial crop area, not previous state
        let newArea = { ...prev }

        if (resizeHandle === 'move') {
          newArea.x = Math.max(0, Math.min(prev.x + deltaX, img.naturalWidth - prev.width))
          newArea.y = Math.max(0, Math.min(prev.y + deltaY, img.naturalHeight - prev.height))
          return newArea
        }

        if (!aspectRatio) {
          switch (resizeHandle) {
            case 'top-left':
              newArea.x = Math.max(0, Math.min(prev.x + deltaX, prev.x + prev.width - minWidth))
              newArea.y = Math.max(0, Math.min(prev.y + deltaY, prev.y + prev.height - minHeight))
              newArea.width = prev.width - (newArea.x - prev.x)
              newArea.height = prev.height - (newArea.y - prev.y)
              break
            case 'top-right':
              newArea.y = Math.max(0, Math.min(prev.y + deltaY, prev.y + prev.height - minHeight))
              newArea.width = Math.max(minWidth, Math.min(prev.width + deltaX, img.naturalWidth - prev.x))
              newArea.height = prev.height - (newArea.y - prev.y)
              break
            case 'bottom-left':
              newArea.x = Math.max(0, Math.min(prev.x + deltaX, prev.x + prev.width - minWidth))
              newArea.width = prev.width - (newArea.x - prev.x)
              newArea.height = Math.max(minHeight, Math.min(prev.height + deltaY, img.naturalHeight - prev.y))
              break
            case 'bottom-right':
              newArea.width = Math.max(minWidth, Math.min(prev.width + deltaX, img.naturalWidth - prev.x))
              newArea.height = Math.max(minHeight, Math.min(prev.height + deltaY, img.naturalHeight - prev.y))
              break
            case 'top':
              newArea.y = Math.max(0, Math.min(prev.y + deltaY, prev.y + prev.height - minHeight))
              newArea.height = prev.height - (newArea.y - prev.y)
              break
            case 'bottom':
              newArea.height = Math.max(minHeight, Math.min(prev.height + deltaY, img.naturalHeight - prev.y))
              break
            case 'left':
              newArea.x = Math.max(0, Math.min(prev.x + deltaX, prev.x + prev.width - minWidth))
              newArea.width = prev.width - (newArea.x - prev.x)
              break
            case 'right':
              newArea.width = Math.max(minWidth, Math.min(prev.width + deltaX, img.naturalWidth - prev.x))
              break
          }
        } else {
          switch (resizeHandle) {
            case 'top-left': {
              const widthFromX = prev.width - deltaX
              const heightFromY = prev.height - deltaY

              let newWidth: number
              let newHeight: number

              if (Math.abs(deltaX) > Math.abs(deltaY)) {
                newWidth = Math.max(minWidth, widthFromX)
                newHeight = newWidth / aspectRatio
              } else {
                newHeight = Math.max(minHeight, heightFromY)
                newWidth = newHeight * aspectRatio
              }
              newArea.x = prev.x + prev.width - newWidth
              newArea.y = prev.y + prev.height - newHeight
              if (newArea.x < 0) {
                newArea.x = 0
                newWidth = prev.x + prev.width
                newHeight = newWidth / aspectRatio
                newArea.y = prev.y + prev.height - newHeight
              }
              if (newArea.y < 0) {
                newArea.y = 0
                newHeight = prev.y + prev.height
                newWidth = newHeight * aspectRatio
                newArea.x = prev.x + prev.width - newWidth
              }

              newArea.width = newWidth
              newArea.height = newHeight
              break
            }
            case 'top-right': {
              const widthFromX = prev.width + deltaX
              const heightFromY = prev.height - deltaY

              let newWidth: number
              let newHeight: number

              if (Math.abs(deltaX) > Math.abs(deltaY)) {
                newWidth = Math.max(minWidth, widthFromX)
                newHeight = newWidth / aspectRatio
              } else {
                newHeight = Math.max(minHeight, heightFromY)
                newWidth = newHeight * aspectRatio
              }

              newArea.y = prev.y + prev.height - newHeight
              if (prev.x + newWidth > img.naturalWidth) {
                newWidth = img.naturalWidth - prev.x
                newHeight = newWidth / aspectRatio
                newArea.y = prev.y + prev.height - newHeight
              }
              if (newArea.y < 0) {
                newArea.y = 0
                newHeight = prev.y + prev.height
                newWidth = newHeight * aspectRatio
              }

              newArea.width = newWidth
              newArea.height = newHeight
              break
            }
            case 'bottom-left': {
              const avgDelta = (-deltaX + deltaY) / 2
              let newWidth = prev.width + avgDelta
              let newHeight = newWidth / aspectRatio
              if (newWidth < minWidth || newHeight < minHeight) {
                newWidth = minWidth
                newHeight = minHeight
              }
              newArea.x = prev.x + (prev.width - newWidth)
              if (newArea.x < 0) {
                newArea.x = 0
                newWidth = prev.x + prev.width
                newHeight = newWidth / aspectRatio
              }
              if (newArea.y + newHeight > img.naturalHeight) {
                newHeight = img.naturalHeight - newArea.y
                newWidth = newHeight * aspectRatio
                newArea.x = prev.x + prev.width - newWidth
              }

              newArea.width = newWidth
              newArea.height = newHeight
              break
            }
            case 'bottom-right': {
              const avgDelta = (deltaX + deltaY) / 2
              let newWidth = prev.width + avgDelta
              let newHeight = newWidth / aspectRatio
              if (newWidth < minWidth || newHeight < minHeight) {
                newWidth = minWidth
                newHeight = minHeight
              }
              if (newArea.x + newWidth > img.naturalWidth) {
                newWidth = img.naturalWidth - newArea.x
                newHeight = newWidth / aspectRatio
              }
              if (newArea.y + newHeight > img.naturalHeight) {
                newHeight = img.naturalHeight - newArea.y
                newWidth = newHeight * aspectRatio
              }

              newArea.width = newWidth
              newArea.height = newHeight
              break
            }
            case 'top':
            case 'bottom': {
              let newHeight = resizeHandle === 'top' ? prev.height - deltaY : prev.height + deltaY
              let newWidth = newHeight * aspectRatio
              if (newHeight < minHeight) {
                newHeight = minHeight
                newWidth = minWidth
              }

              if (resizeHandle === 'top') {
                const potentialY = prev.y + prev.height - newHeight
                if (potentialY < 0) {
                  newArea.y = 0
                  newHeight = prev.y + prev.height
                  newWidth = newHeight * aspectRatio
                } else {
                  newArea.y = potentialY
                }
              } else {
                if (newArea.y + newHeight > img.naturalHeight) {
                  newHeight = img.naturalHeight - newArea.y
                  newWidth = newHeight * aspectRatio
                }
              }
              const widthDiff = newWidth - prev.width
              let newX = prev.x - widthDiff / 2

              if (newX < 0) {
                newX = 0
                newWidth = Math.min(newWidth, img.naturalWidth)
                newHeight = newWidth / aspectRatio
                if (resizeHandle === 'top') {
                  newArea.y = prev.y + prev.height - newHeight
                }
              } else if (newX + newWidth > img.naturalWidth) {
                newX = img.naturalWidth - newWidth
              }

              newArea.x = newX
              newArea.width = newWidth
              newArea.height = newHeight
              break
            }
            case 'left':
            case 'right': {
              let newWidth = resizeHandle === 'left' ? prev.width - deltaX : prev.width + deltaX
              let newHeight = newWidth / aspectRatio
              if (newWidth < minWidth) {
                newWidth = minWidth
                newHeight = minHeight
              }

              if (resizeHandle === 'left') {
                const potentialX = prev.x + prev.width - newWidth
                if (potentialX < 0) {
                  newArea.x = 0
                  newWidth = prev.x + prev.width
                  newHeight = newWidth / aspectRatio
                } else {
                  newArea.x = potentialX
                }
              } else {
                if (newArea.x + newWidth > img.naturalWidth) {
                  newWidth = img.naturalWidth - newArea.x
                  newHeight = newWidth / aspectRatio
                }
              }
              const heightDiff = newHeight - prev.height
              let newY = prev.y - heightDiff / 2

              if (newY < 0) {
                newY = 0
                newHeight = Math.min(newHeight, img.naturalHeight)
                newWidth = newHeight * aspectRatio
                if (resizeHandle === 'left') {
                  newArea.x = prev.x + prev.width - newWidth
                }
              } else if (newY + newHeight > img.naturalHeight) {
                newY = img.naturalHeight - newHeight
              }

              newArea.y = newY
              newArea.width = newWidth
              newArea.height = newHeight
              break
            }
          }
        }

        return newArea
      })
    }

    const handleGlobalMouseUp = () => {
      setIsDragging(false)
      setResizeHandle(null)
    }

    document.addEventListener('mousemove', handleGlobalMouseMove)
    document.addEventListener('mouseup', handleGlobalMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove)
      document.removeEventListener('mouseup', handleGlobalMouseUp)
    }
  }, [isDragging, dragStart, resizeHandle, props.aspectRatio, initialCropArea])

  const handleMouseDown = (e: React.MouseEvent, handle: ResizeHandle) => {
    e.preventDefault()
    e.stopPropagation()
    setResizeHandle(handle)
    setIsDragging(true)
    setDragStart({ x: e.clientX, y: e.clientY })
    setInitialCropArea(cropArea) // Store the crop area at the start of the drag
  }

  const handleError = () => {
    console.error('Failed to load image for cropping')
    setIsSaving(false)
  }

  const handleSave = async () => {
    if (!props.file) return

    const originalFile = props.file // Store file reference to satisfy TypeScript
    setIsSaving(true)

    // Use setTimeout to allow UI to update before heavy processing
    setTimeout(async () => {
      const img = new Image()

      const handleLoad = async () => {
        try {
          // Bound the canvas so a very large crop does not stall the encode.
          const MAX_DIMENSION = 4096 // Maximum width or height
          let canvasWidth = cropArea.width
          let canvasHeight = cropArea.height

          if (canvasWidth > MAX_DIMENSION || canvasHeight > MAX_DIMENSION) {
            const scale = Math.min(MAX_DIMENSION / canvasWidth, MAX_DIMENSION / canvasHeight)
            canvasWidth = Math.floor(canvasWidth * scale)
            canvasHeight = Math.floor(canvasHeight * scale)
          }

          const canvas = document.createElement('canvas')
          canvas.width = canvasWidth
          canvas.height = canvasHeight

          const ctx = canvas.getContext('2d', {
            alpha: false, // Disable alpha channel for better performance
            willReadFrequently: false,
          })

          if (!ctx) {
            console.error('Failed to get canvas context')
            setIsSaving(false)
            return
          }

          ctx.imageSmoothingEnabled = true
          ctx.imageSmoothingQuality = 'high'

          ctx.drawImage(img, cropArea.x, cropArea.y, cropArea.width, cropArea.height, 0, 0, canvasWidth, canvasHeight)

          let quality = 0.92
          const tryConvert = () => {
            canvas.toBlob(
              (blob) => {
                if (!blob) {
                  console.error('Failed to create blob from canvas')
                  setIsSaving(false)
                  return
                }

                if (blob.size > MAX_FILE_SIZE && quality > 0.1) {
                  quality -= 0.1
                  tryConvert()
                  return
                }

                const file = new File([blob], originalFile.name.replace(/\.[^/.]+$/, '.jpg'), {
                  type: 'image/jpeg',
                  lastModified: Date.now(),
                })

                props.onSave(file, cropArea)
                setIsSaving(false)
              },
              // Not WebP: the picture also heads the emails a company signs, and Outlook shows no WebP.
              'image/jpeg',
              quality,
            )
          }

          tryConvert()
        } catch (error) {
          console.error('Error during image processing:', error)
          setIsSaving(false)
        }
      }

      img.addEventListener('error', handleError)
      img.addEventListener('load', handleLoad)
      img.src = imageSrc
    }, 50) // Small delay to allow UI to update
  }

  const scaleX = imageRect && naturalSize ? naturalSize.width / imageRect.width : 1
  const scaleY = imageRect && naturalSize ? naturalSize.height / imageRect.height : 1

  return (
    <Dialog open={props.open} onOpenChange={(open) => !isSaving && props.onOpenChange(open)}>
      <DialogContent className="flex w-fit max-w-[95vw]! flex-col gap-3 p-6" showCloseButton={false}>
        <DialogHeader className="flex flex-row items-center justify-between gap-0">
          <div className="flex flex-col gap-1">
            <DialogTitle>{texts.title}</DialogTitle>
            <DialogDescription>{texts.description}</DialogDescription>
          </div>
          <div className="flex gap-2">
            <Button type="button" onClick={() => props.onOpenChange(false)} variant="outline" disabled={isSaving}>
              {texts.cancel}
            </Button>
            <Button type="button" onClick={handleSave} disabled={isSaving}>
              {isSaving ? (
                <>
                  <LoaderCircleIcon className="size-4 animate-spin" />
                  {/* We're not uploading it yet, but it is a more intuitive text instead of "Converting..." */}
                  {texts.uploading}
                </>
              ) : (
                texts.save
              )}
            </Button>
          </div>
        </DialogHeader>

        <div
          ref={containerRef}
          // `p-2` is the handles' own overhang: they sit `-2` outside the crop rectangle, so at a full-image
          // crop the padding is what keeps them inside the box that `overflow-hidden` (there to contain the
          // overlay's 2000px dimmer shadow) clips to — without it a corner handle is clipped away and cannot
          // be grabbed at all.
          className="relative mx-auto flex w-fit items-center justify-center overflow-hidden rounded-lg p-2"
        >
          {imageSrc && (
            <div className="relative">
              <img
                ref={imageRef}
                src={imageSrc}
                alt=""
                data-testid="image-editor-image"
                className="max-h-[calc(85vh-6rem)] max-w-[calc(90vw-3rem)] object-contain"
                draggable={false}
                onLoad={() => {
                  // Wait for dialog animation to complete before calculating dimensions
                  setTimeout(() => {
                    setImageLoaded(true)
                    if (imageRef.current) {
                      setImageRect(imageRef.current.getBoundingClientRect())
                      setNaturalSize({
                        width: imageRef.current.naturalWidth,
                        height: imageRef.current.naturalHeight,
                      })
                    }
                  }, 150) // Wait for dialog animation (typically 150ms)
                }}
              />

              {imageLoaded && imageRect && naturalSize && (
                <div
                  data-testid="crop-overlay"
                  data-crop-x={Math.round(cropArea.x)}
                  data-crop-y={Math.round(cropArea.y)}
                  data-crop-width={Math.round(cropArea.width)}
                  data-crop-height={Math.round(cropArea.height)}
                  className="border-primary absolute border-2"
                  style={{
                    left: `${cropArea.x / scaleX}px`,
                    top: `${cropArea.y / scaleY}px`,
                    width: `${cropArea.width / scaleX}px`,
                    height: `${cropArea.height / scaleY}px`,
                    boxShadow: '0 0 0 2000px color-mix(in srgb, var(--muted) 25%, transparent)',
                  }}
                >
                  {/* Move handle - center of crop area */}
                  <div
                    role="button"
                    aria-label={t`Move the crop area`}
                    data-testid="crop-move"
                    className="absolute inset-4 flex cursor-move items-center justify-center transition-colors"
                    onMouseDown={(e) => handleMouseDown(e, 'move')}
                  >
                    <MoveIcon className="text-primary size-8 opacity-50" />
                  </div>

                  {RESIZE_HANDLES.map(({ key, className }) => (
                    <div
                      key={key}
                      role="button"
                      aria-label={handleLabels[key]}
                      data-testid={`crop-handle-${key}`}
                      className={className}
                      onMouseDown={(e) => handleMouseDown(e, key)}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <canvas ref={canvasRef} className="hidden" />
      </DialogContent>
    </Dialog>
  )
}
